
import React, { useEffect, useRef, useState } from 'react';

// Hook para animar el contador
function useCountUp(end: number, duration = 1200) {
  const [count, setCount] = useState(0);
  const ref = useRef<number | null>(null);
  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 16);
    function step() {
      start += increment;
      if (start < end) {
        setCount(Math.floor(start));
        ref.current = requestAnimationFrame(step);
      } else {
        setCount(end);
        if (ref.current !== null) cancelAnimationFrame(ref.current);
      }
    }
    ref.current = requestAnimationFrame(step);
    return () => {
      if (ref.current !== null) cancelAnimationFrame(ref.current);
    };
  }, [end, duration]);
  return count;
}

const About: React.FC = () => {
  // Contadores animados
  const countYears = useCountUp(3);
  const countVehicles = useCountUp(5);
  const countClients = useCountUp(85);
  const countCoverage = useCountUp(100);
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
    {/* Hero Section */}
  <section className="relative text-white" style={{ minHeight: '320px' }} data-aos="fade-up">
      <img
        alt="Camión Royal Trucking"
        className="absolute inset-0 w-full h-full object-cover object-center z-0"
        loading="lazy"
        src="/camion3.webp"
        style={{ filter: 'brightness(1.08)' }}
      />
      <div className="absolute inset-0 bg-primary-600/60 z-10"></div>
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center py-20">
        <h1 className="text-4xl lg:text-5xl font-bold mb-6">Sobre Nosotros</h1>
        <p className="text-xl text-primary-100 max-w-3xl mx-auto">
          Somos una empresa de transporte netamente peruana, especializada en el traslado de carga por carretera de mercadería suelta y paletizada.
        </p>
      </div>
    </section>

    {/* Misión, Visión, Valores, Objetivos */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="bg-primary-50 rounded-xl p-8" data-aos="fade-up" data-aos-delay="0">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Misión</h2>
          <p className="text-gray-700 leading-relaxed">Proporcionar soluciones integrales a cada cliente según su necesidad.</p>
        </div>
        <div className="bg-primary-50 rounded-xl p-8" data-aos="fade-up" data-aos-delay="100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Visión</h2>
          <p className="text-gray-700 leading-relaxed">Ser la empresa líder en transporte en el mercado a nivel nacional, siendo la satisfacción del cliente, nuestro mayor orgullo.</p>
        </div>
        <div className="bg-primary-50 rounded-xl p-8 md:col-span-2" data-aos="fade-up" data-aos-delay="200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Valores</h2>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><span className="font-semibold">Excelencia:</span> Nos esforzamos por superar las expectativas en cada servicio, buscando siempre la mejora continua y la calidad en todo lo que hacemos.</li>
            <li><span className="font-semibold">Seguridad:</span> Priorizamos la protección de la carga, la integridad de nuestro equipo y la confianza de nuestros clientes en cada operación.</li>
            <li><span className="font-semibold">Innovación:</span> Adoptamos nuevas tecnologías y procesos para optimizar nuestros servicios y mantenernos a la vanguardia del sector.</li>
            <li><span className="font-semibold">Responsabilidad:</span> Cumplimos nuestros compromisos con ética, puntualidad y transparencia, asumiendo la responsabilidad de cada envío.</li>
            <li><span className="font-semibold">Satisfacción del cliente:</span> Escuchamos y respondemos a las necesidades de nuestros clientes, construyendo relaciones de confianza a largo plazo.</li>
          </ul>
        </div>
        <div className="bg-primary-50 rounded-xl p-8 md:col-span-2" data-aos="fade-up" data-aos-delay="300">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Objetivos</h2>
          <p className="text-gray-700 leading-relaxed">Ser el socio estratégico aliado de tus proyectos y emprendimientos. Haciendo posible que llegues hasta el lugar más recóndito de nuestro territorio nacional.</p>
        </div>
      </div>
    </section>

    {/* Valores Destacados */}
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Nuestros Valores</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Los valores que guían cada decisión y acción en Royal Trucking, formando la base de nuestra cultura empresarial.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Excelencia */}
          <div className="text-center" data-aos="fade-up" data-aos-delay="0">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Excelencia</h3>
            <p className="text-gray-600 text-sm">Buscamos la excelencia en cada aspecto de nuestro servicio, desde la atención al cliente hasta la entrega final.</p>
          </div>
          {/* Seguridad */}
          <div className="text-center" data-aos="fade-up" data-aos-delay="100">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Seguridad</h3>
            <p className="text-gray-600 text-sm">La seguridad de su carga y la de nuestros empleados es nuestra máxima prioridad en todas las operaciones.</p>
          </div>
          {/* Innovación */}
          <div className="text-center" data-aos="fade-up" data-aos-delay="200">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Innovación</h3>
            <p className="text-gray-600 text-sm">Continuamente innovamos en tecnología y procesos para ofrecer servicios más eficientes y confiables.</p>
          </div>
          {/* Responsabilidad */}
          <div className="text-center" data-aos="fade-up" data-aos-delay="300">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Responsabilidad</h3>
            <p className="text-gray-600 text-sm">Asumimos la responsabilidad total de cada envío, garantizando la integridad y puntualidad de la entrega.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Métricas */}
    <section className="py-20 bg-primary-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Royal Trucking en Números</h2>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">Cifras que respaldan nuestra trayectoria y compromiso con la excelencia.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center" data-aos="fade-up" data-aos-delay="0">
            <div className="text-4xl md:text-5xl font-bold mb-2">
              <span>{countYears}+</span>
            </div>
            <div className="text-primary-100">Años de Experiencia</div>
          </div>
          <div className="text-center" data-aos="fade-up" data-aos-delay="100">
            <div className="text-4xl md:text-5xl font-bold mb-2">
              <span>{countVehicles}+</span>
            </div>
            <div className="text-primary-100">Vehículos</div>
          </div>
          <div className="text-center" data-aos="fade-up" data-aos-delay="200">
            <div className="text-4xl md:text-5xl font-bold mb-2">
              <span>{countClients}+</span>
            </div>
            <div className="text-primary-100">Clientes Satisfechos</div>
          </div>
          <div className="text-center" data-aos="fade-up" data-aos-delay="300">
            <div className="text-4xl md:text-5xl font-bold mb-2">
              <span>{countCoverage}%</span>
            </div>
            <div className="text-primary-100">Cobertura Nacional</div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">¿Listo para trabajar con nosotros?</h2>
        <p className="text-xl mb-8 text-gray-600 max-w-2xl mx-auto">Únase a miles de empresas que confían en Royal Trucking para sus necesidades de transporte terrestre de carga.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200" href="/contact">Contactar Ahora</a>
          <a className="border-2 border-primary-600 text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-600 hover:text-white transition-colors duration-200" href="/services">Ver Servicios</a>
        </div>
      </div>
    </section>
  </div>
  );
};

export default About;
